import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import TableSkeleton from "./TableSkeleton";

const MAX_WIDTH_CLASS = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

/**
 * Shared WASL modal shell (backdrop blur, eyebrow title, scroll body, footer slot).
 */
const CallCenterModal = ({
  open,
  onClose,
  blockClose = false,
  eyebrow,
  title,
  subtitle,
  badges = [],
  maxWidth = "2xl",
  maxHeight = "min(92vh,880px)",
  zIndexClass = "z-[200]",
  closeLabel = "Close",
  showCloseButton = true,
  loading = false,
  loadingRows = 5,
  contentVariant = "card",
  footer,
  headerBelow,
  header,
  bodyClassName,
  children,
  ariaLabelledBy: ariaLabelledByProp,
}) => {
  const autoTitleId = useId();
  const titleId = ariaLabelledByProp || autoTitleId;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !blockClose) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, blockClose, onClose]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const widthClass = MAX_WIDTH_CLASS[maxWidth] || MAX_WIDTH_CLASS["2xl"];
  const badgeList = Array.isArray(badges) ? badges.filter(Boolean) : badges ? [badges] : [];

  const bodyContent = loading ? (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <TableSkeleton columns={1} rows={loadingRows} />
    </div>
  ) : contentVariant === "card" ? (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">{children}</div>
  ) : contentVariant === "plain" ? (
    <div className="min-w-0">{children}</div>
  ) : (
    children
  );

  return createPortal(
    <div
      className={`fixed inset-0 overflow-y-auto bg-slate-900/55 backdrop-blur-[2px] ${zIndexClass}`}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !blockClose) onClose?.();
      }}
    >
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div
          className={`flex w-full ${widthClass} flex-col overflow-hidden rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-2xl`}
          style={{ maxHeight }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title || eyebrow ? titleId : undefined}
          onClick={(event) => event.stopPropagation()}
        >
          {header ? (
            <div className="shrink-0 border-b border-slate-200 bg-white">{header}</div>
          ) : (
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
              <div className="min-w-0 flex-1">
                {eyebrow ? (
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#2B7FFF]">{eyebrow}</p>
                ) : null}
                {title ? (
                  <h2
                    id={titleId}
                    className={`truncate font-bold text-slate-900 ${eyebrow ? "mt-1 text-lg" : "text-xl"}`}
                  >
                    {title}
                  </h2>
                ) : null}
                {subtitle ? <p className="mt-1 text-sm leading-relaxed text-slate-500">{subtitle}</p> : null}
                {headerBelow ? <div className="mt-2">{headerBelow}</div> : null}
                {badgeList.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {badgeList.map((badge) => (
                      <span
                        key={badge}
                        className="inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs font-semibold text-slate-700"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              {showCloseButton ? (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={blockClose}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
                  aria-label={closeLabel}
                >
                  <FiX className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          )}

          <div
            className={
              bodyClassName || "min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
            }
          >
            {bodyContent}
          </div>

          {footer ? (
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-white px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallCenterModal;
